import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { AssignStatus } from "@/enum/list.enum";

import {
	renderSelectColumn,
	renderAssignToColumn,
	renderConnectionsColumn,
	renderEstimatedNumEmployeesColumn,
	renderGenderColumn,
	renderIndustryColumn,
	renderLanguageColumn,
	renderNameColumn,
	renderPersonCityColumn,
	renderPersonCountryColumn,
	renderPersonStateColumn,
	renderTitleColumn,
	renderSeniorityColumn,
	renderCompanyColumn,
	renderLiPostContentColumn,
	renderLiPostLikesColumn,
	renderLiPostCommentsColumn,
	renderLiPostSharesColumn,
	renderFoundedYearColumn,
	renderCompanyCityColumn,
	renderCompanyStateColumn,
	renderCompanyCountryColumn,
	renderFollowers,
	renderSourceNameColumn,
	renderLiPostContentTypeColumn,
	renderCompanyPeopleCheckColumn,
	renderLiPostContentResponseColumn,
	renderAboutColumn,
	renderHeadlineColumn,
	renderCreatedAt,
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderExclusionReasonColumn,
	renderPreviousEmployersColumn,
	renderLiPostAuthorColumn,
	renderLiPostAuthorCompanyColumn,
	renderCompanyCategoryColumn,
	renderCompanyScoreColumn,
	renderPersonaCategoryColumn,
	renderPersonaScoreColumn,
	renderReportetEmployeesColumn,
	renderRoleChangeColumn,
	renderPreviousJobTitleColumn,
	renderPreviousEmployerColumn,
	renderCampaignAddedAt,
	renderAssignedStatusColumn,
	renderLastNodeTypeColumn,
	renderLastScheduledAtColumn,
	renderLastMessageColumn,
	renderDeepResearchStatusColumn,
} from "../../utils/allColumns";
import {
	renderCompanyOtherListsColumn,
	renderOtherListsColumn,
} from "../../utils/otherListsColumns";
import { renderMaybeColumn } from "../../utils/renderMaybeColumn";
import { GetIrlColumns } from "../CustomerImplementations/IrlColumns";

export interface LiPostTableColumnsProps {
	selectAll: boolean;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	integrations: { [key: string]: boolean | undefined };
	handleSelectCampaignAndProspect: (
		id: number | string,
		listPersonId: number,
		type?: "internal" | "lemlist"
	) => void;
	assignStatus: AssignStatus;
	onResearchClick?: (row: import("../../schema/list").ListPerson) => void;
	onOpenDetail?: (row: import("../../schema/list").ListPerson) => void;
}

export const LiPostTableColumns = ({
	selectAll,
	setShowAll,
	toggleSelectAll,
	selectedItems,
	handleSelectItem,
	handleCopyClick,
	ts,
	sortingInbox,
	setShowEdit,
	showEdit,
	integrations,
	handleSelectCampaignAndProspect,
	assignStatus,
	onResearchClick,
	onOpenDetail,
}: LiPostTableColumnsProps): TableColumn[] => {
	const columns: TableColumn[] = [
		renderSelectColumn(setShowAll, toggleSelectAll, selectAll, selectedItems, handleSelectItem),
		renderMaybeColumn(ts),
		renderAssignToColumn(ts, handleCopyClick, handleSelectCampaignAndProspect),
		renderNameColumn(ts, handleCopyClick, sortingInbox, onOpenDetail),
		renderCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderExclusionReasonColumn(ts, sortingInbox),
		renderDeepResearchStatusColumn(onResearchClick),
		renderCompanyPeopleCheckColumn(ts),
		renderLiPostContentTypeColumn(ts, sortingInbox),
		renderLiPostContentColumn(ts, handleCopyClick, sortingInbox),
		renderLiPostContentResponseColumn(ts, handleCopyClick, sortingInbox),
		renderLiPostLikesColumn(ts, sortingInbox),
		renderLiPostCommentsColumn(ts, sortingInbox),
		renderLiPostSharesColumn(ts, sortingInbox),
	];

	columns.push(renderGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit));
	columns.push(renderSalutationDeColumn(ts));
	columns.push(renderSalutationEnColumn(ts));
	columns.push(renderTitleColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderHeadlineColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderAboutColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderSeniorityColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderLanguageColumn(ts, sortingInbox));
	columns.push(renderPersonCityColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderPersonStateColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderPersonCountryColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCompanyCityColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCompanyStateColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCompanyCountryColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderEstimatedNumEmployeesColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderReportetEmployeesColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderFoundedYearColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderIndustryColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderConnectionsColumn(ts, sortingInbox));
	columns.push(renderFollowers(ts, sortingInbox));
	columns.push(renderOtherListsColumn(ts, sortingInbox));
	columns.push(renderCompanyOtherListsColumn(ts, sortingInbox));
	columns.push(renderSourceNameColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderCreatedAt(ts, sortingInbox));
	columns.push(renderPreviousEmployersColumn(ts));
	columns.push(renderPreviousEmployerColumn(ts));
	columns.push(renderPreviousJobTitleColumn(ts));

	columns.push(renderRoleChangeColumn());
	columns.push(renderLiPostAuthorColumn(ts, handleCopyClick, sortingInbox));
	columns.push(renderLiPostAuthorCompanyColumn(ts, handleCopyClick, sortingInbox));

	if (assignStatus === AssignStatus.ASSIGNED) {
		columns.push(renderCampaignAddedAt(ts, sortingInbox));
		columns.push(renderAssignedStatusColumn(ts, sortingInbox));
		columns.push(renderLastNodeTypeColumn(ts, sortingInbox));
		columns.push(renderLastScheduledAtColumn(ts, sortingInbox));
		columns.push(renderLastMessageColumn(ts));
	}

	if (integrations.LEAD_SCORING) {
		columns.push(renderPersonaCategoryColumn(sortingInbox));
		columns.push(renderCompanyCategoryColumn(sortingInbox));
		columns.push(renderPersonaScoreColumn(sortingInbox));
		columns.push(renderCompanyScoreColumn(sortingInbox));
	}

	if (integrations.IRL_COUNTRIES_SUM) {
		const irlColumns = GetIrlColumns(ts);

		columns.push(...irlColumns);
	}

	return columns;
};
